/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    ICommerceApiSettings,
    ICreateActionContext,
    isChannelTypeB2B
} from '@msdyn365-commerce/core';
import {
    CatalogsDataActions,
    ChannelReferenceAdditionalLookupCriteria,
    ChannelReferenceLookupCriteria,
    ExtensibleEnumeration,
    FeatureState,
    IDictionary,
    ProductDeliveryOptions,
    ProductsDataActions,
    SalesOrder,
    SalesOrdersDataActions,
    SearchLocation,
    SimpleProduct,
    StoreOperationsDataActions,
    TransactionType,
    SalesLine
} from '@msdyn365-commerce/retail-proxy';
import {
    ArrayExtensions,
    buildCacheKey,
    getSimpleProducts,
    ObjectExtensions,
    ProductInput,
    QueryResultSettingsProxy
} from '@msdyn365-commerce-modules/retail-actions';

import {
    getFeatureStatesAsync,
    getOnlineChannelAsync
} from '@msdyn365-commerce/retail-proxy/dist/DataActions/StoreOperationsDataActions.g';
import {
    getSalesOrderDetailsByChannelReferenceIdAction,
    GetSalesOrderDetailsByConfirmationInput
} from './get-sales-order-details-by-confirmation-id';
/**
 * ISalesOrderWithHydrations.
 */
interface ISalesOrderWithHydrations {
    salesOrder: SalesOrder;
    products: SimpleProduct[];
    productDeliveryOptions?: ProductDeliveryOptions[];
    isSameSiteType?: boolean;
}

/**
 * Product and Catalog Interface.
 */
interface IProductCatalog {
    productId: number;
    catalogId: number | undefined;
}

/**
 * AdditionalLookupCriteria entity interface.
 */
export interface IAdditionalLookupCriteria {
    orderStatusLookUpParameterTypeName: string;
    orderStatusLookUpParameterValue: string;
}

/**
 * OrderTypes Types of orders.
 */
export enum orderTypes {
    salesOrder = 'salesOrder',
    transaction = 'transaction'
}

/**
 * GetQueryStringParameters.
 * @param context -- URL.
 * @returns Additional lookup criteria -- IAdditionalLookupCriteria[].
 */
const getQueryStringParameters = (context: IActionContext): IAdditionalLookupCriteria[] => {
    const addtionalLookupParameter: IAdditionalLookupCriteria[] = [];
    let query: IDictionary<string>;
    if (context.requestContext.query) {
        query = context.requestContext.query;
        const queryArray = Object.entries(query);

        for (let index = 1; index <= queryArray.length; index++) {
            const additionalFieldName: string = `field${index}Name`;
            const additionalFieldValue = `field${index}Value`;
            if (additionalFieldName in query && additionalFieldValue in query) {
                addtionalLookupParameter.push({
                    orderStatusLookUpParameterTypeName: query[additionalFieldName],
                    orderStatusLookUpParameterValue: query[additionalFieldValue]
                });
            }
        }
    }
    return addtionalLookupParameter;
};

/**
 * Calls the Retail API and returns the sales order for anonymous user.
 * @param confirmationId -- Confirmation Id.
 * @returns SalesOrder -- Promise<SalesOrder>.
 */
const getSalesOrderDetailsByLookupCriteria = (confirmationId: string) => async (context: IActionContext): Promise<SalesOrder> => {
    const { propertyName = '', propertyValue = '' } = context.requestContext.query ?? {};

    const addtionalLookupParameter: IAdditionalLookupCriteria[] = getQueryStringParameters(context);

    const getExtensibleEnumerations = await StoreOperationsDataActions.getExtensibleEnumerationsAsync({ callerContext: context });

    const orderLookupExtensibleEnum: ExtensibleEnumeration[] =
        getExtensibleEnumerations.find(item => item.TypeName === 'OrderLookupAdditionalCriteriaType')?.ExtensibleEnumerations ?? [];

    const channelReferenceAdditionalLookupCriteria: ChannelReferenceAdditionalLookupCriteria[] = [];

    for (const item of orderLookupExtensibleEnum) {
        const retailProxyValue: string = item.Name.toLocaleLowerCase();
        const retailProxyTypeValue: number = item.Value;
        const orderLookupSearchPropertyName: string = propertyName.toLocaleLowerCase();
        if (retailProxyValue === orderLookupSearchPropertyName) {
            channelReferenceAdditionalLookupCriteria.push({
                OrderLookupAdditionalCriteriaTypeValue: item.Value,
                OrderLookupAdditionalCriteriaTypeParameterValue: propertyValue
            });
        }
        if (ArrayExtensions.hasElements(addtionalLookupParameter)) {
            for (const additionalItem of addtionalLookupParameter) {
                if (retailProxyValue === additionalItem.orderStatusLookUpParameterTypeName.toLocaleLowerCase()) {
                    channelReferenceAdditionalLookupCriteria.push({
                        OrderLookupAdditionalCriteriaTypeValue: retailProxyTypeValue,
                        OrderLookupAdditionalCriteriaTypeParameterValue: additionalItem.orderStatusLookUpParameterValue
                    });
                }
            }
        }
    }

    const channelReferenceLookupCriteria: ChannelReferenceLookupCriteria = {
        ChannelReferenceId: confirmationId,
        ChannelReferenceAdditionalLookupCriteria: channelReferenceAdditionalLookupCriteria
    };

    const input = new GetSalesOrderDetailsByConfirmationInput(channelReferenceLookupCriteria, context.requestContext.apiSettings);

    return getSalesOrderDetailsByChannelReferenceIdAction(input, context);
};

/**
 * Calls the Retail API and returns the sales order.
 * @param orderType -- Order Type.
 * @param orderId -- Order id.
 * @returns SalesOrder -- Promise<SalesOrder>.
 */
const getSalesOrder = (orderType: string = '', orderId: string = '') => async (context: IActionContext): Promise<SalesOrder> => {
    const { confirmationId = '' } = context.requestContext.query ?? {};

    if (confirmationId) {
        return getSalesOrderDetailsByLookupCriteria(confirmationId)(context);
    } else if (orderType === orderTypes.salesOrder) {
        return SalesOrdersDataActions.getSalesOrderDetailsBySalesIdAsync({ callerContext: context }, orderId);
    }

    return SalesOrdersDataActions.getSalesOrderDetailsByTransactionIdAsync({ callerContext: context }, orderId, SearchLocation.All);
};

/**
 * Calls the Retail API and returns the products.
 * @param productCatalog - The product and catalog ids of the product to retrieve.
 * @param channelId - The channel id for the products.
 * @returns Simple product data.
 */
const getProductsWithCatalog = (productCatalog: IProductCatalog[], channelId?: number) => async (
    context: IActionContext
): Promise<SimpleProduct[]> => {
    const productInputs = productCatalog.map(
        index =>
            new ProductInput(
                index.productId,
                context.requestContext.apiSettings,
                channelId,
                undefined,
                context.requestContext,
                index.catalogId
            )
    );
    return getSimpleProducts(productInputs, context);
};

/**
 * Action input.
 */
export class GetSalesOrderWithHydrationsInput implements IActionInput {
    public orderType: string | undefined;

    public orderId: string | undefined;

    public confirmationId: string | undefined;

    private readonly apiSettings: ICommerceApiSettings;

    public constructor(
        orderType: string | undefined,
        orderId: string | undefined,
        apiSettings: ICommerceApiSettings,
        confirmationId?: string
    ) {
        this.orderType = orderType;
        this.orderId = orderId;
        this.apiSettings = apiSettings;
        this.confirmationId = confirmationId;
    }

    /**
     * GetCacheKey.
     * @returns String.
     */
    public getCacheKey = (): string => buildCacheKey('SalesOrderWithHydrations', this.apiSettings);

    /**
     * GetCacheObjectType.
     * @returns String.
     */
    public getCacheObjectType = (): string =>
        `SalesOrderWithHydrations-${this.orderType ?? ''}-${this.orderId ?? ''}-${this.confirmationId ?? ''}`;

    /**
     * DataCacheType.
     * @returns CacheType.
     */
    public dataCacheType = (): CacheType => 'request';
}

/**
 * Creates the input required to make the retail api call.
 * @param inputData -- InputData as ICreateActionContext.
 * @returns Action Input.
 */
const createSalesOrderWithHydrationsInput = (inputData: ICreateActionContext) => {
    const { salesId = '', transactionId = '', confirmationId = '' } = inputData.requestContext.query ?? {};

    if (confirmationId) {
        const orderType = undefined;
        const orderId = undefined;
        return new GetSalesOrderWithHydrationsInput(orderType, orderId, inputData.requestContext.apiSettings, confirmationId);
    } else if (salesId) {
        return new GetSalesOrderWithHydrationsInput(orderTypes.salesOrder, salesId, inputData.requestContext.apiSettings);
    } else if (transactionId) {
        return new GetSalesOrderWithHydrationsInput(orderTypes.transaction, transactionId, inputData.requestContext.apiSettings);
    }

    throw new Error('createSalesOrderWithHydrationsInput - No salesId, confirmationId or transactionId provided.');
};

/**
 * Get sales order with hydrations action.
 * @param input -- Input.
 * @param context -- Context.
 * @returns ISalesOrderWithHydrations.
 */
export async function getSalesOrderWithHydrationsAction(
    input: GetSalesOrderWithHydrationsInput,
    context: IActionContext
): Promise<ISalesOrderWithHydrations> {
    const { orderType, orderId } = input;

    const { confirmationId = '' } = context.requestContext.query ?? {};

    if (context.requestContext.user.isAuthenticated && (!orderType || !orderId) && !confirmationId) {
        context.trace('[getSalesOrderWithHydrationsAction] No orderType or orderId or confirmationId provided.');
        return <ISalesOrderWithHydrations>{};
    }

    const salesOrder = await getSalesOrder(orderType, orderId)(context);
    if (salesOrder && salesOrder.SalesLines) {
        salesOrder.SalesLines = salesOrder.SalesLines.filter((saleline: SalesLine) => !saleline.IsVoided);
    }

    if (!salesOrder) {
        context.trace('[getSalesOrderWithHydrationsAction] No salesOrder found.');
        return <ISalesOrderWithHydrations>{};
    }

    // If it is salesInvoice, return salesOrder and empty products
    if (salesOrder.TransactionTypeValue === TransactionType.SalesInvoice) {
        return {
            salesOrder,
            products: []
        };
    }
    let isEnabled: boolean = true;

    if (salesOrder.ChannelId !== context.requestContext.apiSettings.channelId && salesOrder.ChannelId !== undefined) {
        context.trace('[getSalesOrderWithHydrationsAction] SalesOrder is not from current channel.');
        try {
            const channelInfo = await getOnlineChannelAsync({ callerContext: context }, salesOrder.ChannelId);
            const salesOrderChannelType = channelInfo?.OnlineStoreCustomerTypeValue;
            isEnabled = salesOrderChannelType === 1 && isChannelTypeB2B(context.requestContext);
        } catch (error) {
            console.log('Please check the channel type if it is not online, 400 expected.', error);
        }
    }

    if (!ArrayExtensions.hasElements(salesOrder.SalesLines)) {
        context.trace('[getSalesOrderWithHydrationsAction] No salesLine found.');
        return <ISalesOrderWithHydrations>{};
    }

    const productCatalogIds: IProductCatalog[] = salesOrder.SalesLines.map(salesLine => {
        return {
            productId: salesLine.ProductId !== undefined ? salesLine.ProductId : 0,
            catalogId: salesLine.CatalogId
        };
    });

    if (!ArrayExtensions.hasElements(productCatalogIds)) {
        context.trace('[getSalesOrderWithHydrationsAction] No products in saleLines found.');
        return <ISalesOrderWithHydrations>{};
    }

    const featureStates = await getFeatureStatesAsync({ callerContext: context }, [
        'Dynamics.AX.Application.ChannelMultipleCatalogsFeature'
    ]);
    const isCatalogsFeatureEnabled =
        featureStates?.find((featureState: FeatureState) => featureState.Name === 'Dynamics.AX.Application.ChannelMultipleCatalogsFeature')
            ?.IsEnabled || false;

    let products: SimpleProduct[] = [];

    if (!isCatalogsFeatureEnabled) {
        products = await getProductsWithCatalog(productCatalogIds, salesOrder.ChannelId)(context);
    } else {
        // Get product catalogs for current user. Catalog ids will be stored in RecordId property of userCatalogIds.
        const userCatalogIds = await CatalogsDataActions.getCatalogsAsync(
            { callerContext: context },
            context.requestContext.apiSettings.channelId,
            true
        );

        // Filter productCatalogIds to get only those that are in userCatalogIds.
        const matchingProductCatalogIds = productCatalogIds.filter(productCatalogId => {
            return userCatalogIds.some(userCatalogId => userCatalogId.RecordId === productCatalogId.catalogId);
        });

        // Filter productCatalogIds to get only those that are NOT in userCatalogIds.
        const nonMatchingProductCatalogIds = productCatalogIds.filter(productCatalogId => {
            return userCatalogIds.some(userCatalogId => userCatalogId.RecordId !== productCatalogId.catalogId);
        });

        let matchingProducts: SimpleProduct[] = [];
        if (matchingProductCatalogIds.length !== 0) {
            matchingProducts = await getProductsWithCatalog(
                matchingProductCatalogIds,
                context.requestContext.apiSettings.channelId
            )(context);
        }

        let nonMatchingProducts: SimpleProduct[] = [];
        if (nonMatchingProductCatalogIds.length !== 0) {
            nonMatchingProducts = await getProductsWithCatalog(nonMatchingProductCatalogIds, salesOrder.ChannelId)(context);
        }

        products = [...matchingProducts, ...nonMatchingProducts];
    }

    if (!ArrayExtensions.hasElements(products)) {
        context.trace('[getSalesOrderWithHydrationsAction] No simple products found.');
        return <ISalesOrderWithHydrations>{};
    }
    const validProducts = products.filter(product => !ObjectExtensions.isNullOrUndefined(product.RecordId));

    const deliveryOptions = await ProductsDataActions.getDeliveryOptionsAsync(
        {
            callerContext: context,
            queryResultSettings: QueryResultSettingsProxy.getPagingFromInputDataOrDefaultValue(context)
        },
        validProducts.map(product => product.RecordId),
        {},
        4
    )
        .then(result => {
            return result;
        })
        .catch((error: Error) => {
            context.trace(error.message);
            context.telemetry.exception(error);
            context.telemetry.debug('[getDeliveryOptionsForCartLineItems]Error executing action');
            throw new Error('[getDeliveryOptionsForCartLineItems]Error executing action');
        });

    return <ISalesOrderWithHydrations>{
        salesOrder,
        products,
        deliveryOptions,
        isSameSiteType: isEnabled
    };
}

/**
 * GetSalesOrderWithHydrationsAction Data Action.
 * @param options -- Options.
 */
export const getSalesOrderWithHydrationsActionDataAction = createObservableDataAction({
    id: '@msdyn365-commerce-modules/order-management/get-sales-order-with-hydrations',
    action: getSalesOrderWithHydrationsAction as IAction<ISalesOrderWithHydrations>,
    input: createSalesOrderWithHydrationsInput
});

export default getSalesOrderWithHydrationsActionDataAction;
